/**
 * Browser-side client for IG COM (Shopify) data.
 *
 * There is no Shopify token in here, and there must never be one: everything
 * VITE_-prefixed is compiled into the public bundle. The token lives in the
 * Static Web App application settings and is used only by
 * `api/src/functions/igcomOrders.ts`.
 *
 * Contact details arrive already masked for callers who are not on the
 * IGCOM_PII_USERS allowlist — the server decides, not this file, so there is
 * nothing here worth tampering with.
 */

import { getApiTokenSilent } from "../../shell/auth/tokens";
import { apiAuthHeaders } from "../../lib/apiAuthHeaders";

export interface IgcomLineItem {
  title: string;
  sku: string;
  quantity: number;
}

export interface IgcomOrder {
  id: string;
  name: string;
  createdAt: string;
  financialStatus: string;
  fulfillmentStatus: string;
  total: number;
  currency: string;
  customerKey: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  city: string;
  province: string;
  lineItems: IgcomLineItem[];
}

export interface IgcomCustomer {
  key: string;
  name: string;
  email: string;
  phone: string;
  city: string;
  province: string;
  orders: number;
  revenue: number;
  firstOrder: string;
  lastOrder: string;
}

export interface IgcomSummary {
  orderCount: number;
  customerCount: number;
  revenue: number;
  avgOrderValue: number;
  currency: string;
  repeatCustomers: number;
}

export interface IgcomOverview {
  shop: string;
  asOf: string;
  windowDays: number;
  /** False → emails/phones on screen are redacted server-side. */
  piiUnmasked: boolean;
  /** True → more orders exist than the page cap returned. */
  truncated: boolean;
  orders: IgcomOrder[];
  customers: IgcomCustomer[];
  summary: IgcomSummary;
}

async function readError(res: Response): Promise<string> {
  try {
    const body = await res.json();
    return (body as { error?: string }).error || `Request failed (${res.status}).`;
  } catch {
    return `Request failed (${res.status}).`;
  }
}

export async function fetchIgcomOverview(days = 90): Promise<IgcomOverview> {
  const token = await getApiTokenSilent();
  const res = await fetch(`/api/igcom/orders?days=${encodeURIComponent(days)}`, {
    headers: apiAuthHeaders(token),
  });
  if (!res.ok) throw new Error(await readError(res));
  return (await res.json()) as IgcomOverview;
}
